/**
 * Live homepage. Same storefront look Tammy approved on /preview.
 */

import { useEffect, useState } from 'react';
import PromoSection from '../components/PromoSection';
import EventBanner from '../components/EventBanner';
import ActiveCollections from '../components/ActiveCollections';
import EventCategoriesSection from '../components/EventCategoriesSection';
import Testimonials from '../components/Testimonials';
import FacebookSection from '../components/FacebookSection';
import HelpfulGuides from '../components/HelpfulGuides';
import EmailSignupWidget from '../components/EmailSignupWidget';
import { BakerySchema, WebsiteSchema } from '../components/StructuredData';
import { applySeo } from '../utils/seo';
import { HOME_SEO } from '../utils/seoDefaults';
import { PREVIEW_HERO_IMAGE } from './preview/previewAssets';
import { seasonalHeroCopy, type SeasonalHeroCopy } from './preview/seasonalHero';
import { usePreviewHomeData } from './preview/usePreviewHomeData';
import PreviewHero from './preview/PreviewHero';
import PreviewVisitStrip from './preview/PreviewVisitStrip';
import { PreviewCampaigns, PreviewTreats } from './preview/PreviewTreats';
import PreviewCustomCakes from './preview/PreviewCustomCakes';
import PreviewCta from './preview/PreviewCta';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function HomePreview() {
  const { featured, cakes, campaigns, contact } = usePreviewHomeData();
  const [seasonal, setSeasonal] = useState<SeasonalHeroCopy | null>(null);

  useEffect(() => {
    // Live homepage SEO. Tammy can still override title/description in Homepage Editor.
    const applyHomeSeo = (title: string, description: string, keywords?: string, ogImage?: string) => {
      applySeo({
        title,
        description,
        keywords,
        canonicalPath: HOME_SEO.canonicalPath,
        og: {
          type: 'website',
          title,
          description,
          image: ogImage || PREVIEW_HERO_IMAGE,
        },
      });
    };

    applyHomeSeo(HOME_SEO.title, HOME_SEO.description, HOME_SEO.keywords);

    // Season hero is optional. A failed fetch keeps the default headline.
    fetch(`${API_URL}/api/seasons/active-hero`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setSeasonal(seasonalHeroCopy(data?.hero)))
      .catch(() => undefined);

    fetch(`${API_URL}/api/homepage-content`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        applyHomeSeo(
          data.meta_title || HOME_SEO.title,
          data.meta_description || HOME_SEO.description,
          data.meta_keywords || HOME_SEO.keywords,
          data.og_image_url || PREVIEW_HERO_IMAGE
        );
      })
      .catch(() => undefined);
  }, []);

  return (
    <div className="overflow-hidden">
      <BakerySchema />
      <WebsiteSchema />
      <PreviewHero contact={contact} seasonal={seasonal} />
      <PreviewVisitStrip contact={contact} />
      <PromoSection />
      <EventBanner className="min-h-[280px] md:min-h-[360px]" autoScrollInterval={6000} />
      <ActiveCollections />
      <EventCategoriesSection />
      <PreviewCampaigns campaigns={campaigns} />
      <PreviewTreats products={featured} />
      <PreviewCustomCakes images={cakes} contact={contact} />
      <Testimonials />
      <FacebookSection />
      <HelpfulGuides className="section-padding bg-white" />
      <EmailSignupWidget />
      <PreviewCta contact={contact} />
    </div>
  );
}
